package fr.frux.feature.map

import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.core.maps.LocationPermissionStatus
import fr.frux.core.maps.LocationProvider
import fr.frux.core.maps.LocationResult
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.Tree
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.withTimeoutOrNull

private const val TAG = "LocationController"

/**
 * Budget maximal d'une demande de position (#128). Doit rester superieur a la chaine de repli de
 * l'implementation Android (6 s de fix frais + 2 s de `lastLocation`, cf. #50), sinon il la
 * couperait avant que le repli ne joue.
 */
internal const val LOCATION_TIMEOUT_MS = 12_000L

/**
 * Position, permissions et chaine de repli (#186). Isole du reste du ViewModel la demande de
 * position, son budget, et la reaction aux transitions de permission — le VM lui delegue ces deux
 * chemins et garde la main sur l'etat partage (camera, lot charge) via les callbacks fournis.
 */
internal class LocationController(
    private val locationProvider: LocationProvider,
    private val launchForUser: (suspend CoroutineScope.() -> Unit) -> Job,
    private val state: MutableStateFlow<MapState>,
    private val displayedTrees: () -> List<Tree>,
    private val lastLoadedTrees: () -> List<Tree>,
    private val lastLoadedFilter: () -> MapFilter,
    private val armFraming: () -> Unit,
    private val frameVisibleResults: suspend () -> Unit,
    private val mapVisible: Flow<Boolean>,
    private val loadNearbyTrees: (Location) -> Unit,
    private val timeoutMs: Long = LOCATION_TIMEOUT_MS,
) {
    /** Dernier statut de permission recu, pour ne reagir qu'aux transitions reelles. */
    private var lastPermissionStatus: LocationPermissionStatus? = null

    /**
     * Permission accordee d'apres le dernier statut recu. Pilote le suivi, avec la visibilite de la
     * carte : il se coupe des qu'un refus arrive et reprend a l'accord suivant, sans que l'UI ait
     * a relancer la collecte.
     */
    private val permissionGranted = MutableStateFlow(false)

    /**
     * Intention posee par un clic sur « ma position » quand la permission manque encore : l'accord
     * qui suit doit localiser, vue restauree ou non. Sans ce relais, l'utilisateur devait cliquer
     * une seconde fois apres la boite de dialogue (web, ou Android apres un refus).
     */
    private var locateAfterGrant = false

    fun onLocateRequestedPendingPermission() {
        locateAfterGrant = true
        state.update { it.copy(locationPromptSuppressed = false) }
    }

    /**
     * Fin de l'accueil sans position (#502) : la personne vient de répondre, un bandeau de refus
     * à l'arrivée sur la carte ou une nouvelle demande au lancement contrediraient son choix.
     * Levé par tout geste sur « ma position », qui rouvre le chemin normal, ou par un accord.
     */
    fun onLocationSettledByOnboarding() {
        // Une seule fois par session : MapScreen rejoue son effet de montage a chaque retour
        // d'onglet avec le meme drapeau, et ne doit pas rearmer une suppression que la personne
        // a deja levee d'un geste.
        if (onboardingSettlementApplied) return
        onboardingSettlementApplied = true
        state.update { it.copy(locationPromptSuppressed = true) }
    }

    /** Tout tap sur « ma position » rouvre le chemin normal, quel que soit le statut courant. */
    fun onLocateTapped() {
        state.update { it.copy(locationPromptSuppressed = false) }
    }

    private var onboardingSettlementApplied = false

    /**
     * Fait suivre le point bleu a la personne (#734). Suspend tant qu'il est collecte : l'UI le
     * lance quand l'app est au premier plan et l'annule en la quittant, ce qui arrete le GPS. Le
     * suivi est en plus suspendu tant que la carte n'est pas visible (autre onglet, panneau
     * plein ecran) : personne ne verrait le point, et une invitation evaluee ailleurs serait perdue.
     *
     * N'ecrit que [MapState.userLocation]. Ni camera, ni rechargement, ni bandeau : la position
     * bouge toutes les quelques secondes, et recadrer la carte sous un doigt qui la tient serait
     * pire que le defaut corrige.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    suspend fun followUserLocation() {
        combine(permissionGranted, mapVisible) { granted, visible -> granted && visible }
            .distinctUntilChanged()
            .flatMapLatest { follow -> if (follow) locationProvider.locationUpdates() else emptyFlow() }
            .collect { location -> state.update { it.copy(userLocation = location) } }
    }

    /**
     * Reaction au resultat de la demande de permission, transmise par l'UI.
     *
     * GRANTED n'agit que lorsqu'il est nouveau : le ViewModel etant permanent (#124), l'ecran
     * carte re-signale le meme statut a chaque retour d'onglet, et relocaliser a chaque fois
     * recadrerait la carte sous l'utilisateur qui revient. Les refus, eux, se re-signalent a
     * dessein — c'est le chemin qui re-affiche le bandeau apres sa fermeture.
     */
    fun onPermissionResult(
        status: LocationPermissionStatus,
        viewWasRestored: Boolean,
    ) {
        val isNewStatus = status != lastPermissionStatus
        lastPermissionStatus = status
        permissionGranted.value = status == LocationPermissionStatus.GRANTED
        val requestedByUser = locateAfterGrant
        locateAfterGrant = false
        when (status) {
            LocationPermissionStatus.GRANTED -> {
                state.update { it.copy(locationPromptSuppressed = false) }
                // Une vue memorisee prime sur la position : l'utilisateur a choisi ce cadre au
                // lancement precedent, le lui reprendre au demarrage serait exactement ce que le
                // reste de ce ViewModel s'attache a ne pas faire. « Me localiser » reste le
                // chemin explicite, et un accord obtenu depuis ce bouton en fait partie.
                if (requestedByUser || (isNewStatus && !viewWasRestored)) refreshLocation()
            }

            // Un refus deja donne dans l'accueil (#502) ne se re-affiche pas tant que la personne
            // n'a pas elle-meme touche « ma position ».
            LocationPermissionStatus.DENIED -> {
                if (!state.value.locationPromptSuppressed) {
                    state.update { it.copy(locationNotice = LocationNotice.PERMISSION_DENIED) }
                }
            }

            LocationPermissionStatus.PERMANENTLY_DENIED -> {
                if (!state.value.locationPromptSuppressed) {
                    state.update { it.copy(locationNotice = LocationNotice.PERMISSION_PERMANENTLY_DENIED) }
                }
            }

            LocationPermissionStatus.NOT_REQUESTED -> {
                Unit
            }
        }
    }

    /**
     * Demande une position et recentre la carte si elle est obtenue.
     *
     * L'attente est exposee ([MapState.isLocating]) : un fix frais prend plusieurs secondes, et
     * sans retour l'utilisateur ne sait pas si son tap a porte — il retape, et empile des demandes
     * concurrentes (#128). D'ou aussi le garde : une demande a la fois.
     */
    fun refreshLocation() {
        if (state.value.isLocating) {
            Logger.d(TAG, "Localisation deja en cours, tap ignore")
            return
        }
        launchForUser {
            state.update { it.copy(isLocating = true, canSearchArea = false) }
            // Toujours le meme enchainement, quel que soit l'etat de la carte : d'abord cadrer
            // tout ce qu'on connait deja — derniere position connue et derniers arbres charges —
            // puis rafraichir. Les cas particuliers d'autrefois rendaient le bouton imprevisible :
            // selon le deplacement, il recadrait, ou plongeait sur la position, ou ne faisait rien.
            //
            // Un deplacement vers une zone sans arbres a pu vider le lot : remettre le dernier
            // lot charge, sinon la camera reviendrait sur la bonne vue mais sans son contenu. Le
            // rafraichissement le remplacera par des donnees fraiches juste apres.
            if (
                displayedTrees().isEmpty() &&
                lastLoadedTrees().isNotEmpty() &&
                lastLoadedFilter().query == state.value.filter.query
            ) {
                state.update { it.copy(nearbyTrees = lastLoadedTrees()) }
            }
            armFraming()
            frameVisibleResults()
            try {
                // Garde-fou de dernier ressort, pas le budget nominal : l'implementation Android se
                // borne deja elle-meme (fix frais puis repli lastLocation, #50). Ce budget doit lui
                // rester superieur, sinon il couperait le repli avant qu'il ne joue.
                val fresh =
                    withTimeoutOrNull(timeoutMs) {
                        locationProvider.currentLocation()
                    }
                val result = fresh.orLastKnown()
                if (result is LocationResult.Available) {
                    Logger.i(TAG, "Position obtenue")
                    state.update {
                        it.copy(userLocation = result.location, locationNotice = null)
                    }
                } else {
                    Logger.i(TAG, "Pas de position (résultat: $result)")
                    state.update { it.copy(locationNotice = noticeFor(result)) }
                }

                // Le rafraichissement : recharger autour de la position, et cadrer le resultat.
                //
                // La derniere position connue sert de repli quand le fix echoue — sans quoi le
                // bouton resterait inerte, et la carte garderait le lot vide de la zone quittee.
                // Le cadrage final vient du chargement ; il ne bouge la camera que s'il cadre
                // autre chose que le cadrage d'entree (« refresh si besoin »).
                val center = (result as? LocationResult.Available)?.location ?: state.value.userLocation
                if (center != null) {
                    armFraming()
                    loadNearbyTrees(center)
                }
            } finally {
                state.update { it.copy(isLocating = false, locateSequence = it.locateSequence + 1) }
            }
        }
    }

    /**
     * Repli sur la derniere position connue quand le fix frais n'a rien rendu (#606). Seul
     * Android l'appliquait dans son provider : iOS et le web affichaient le bandeau alors qu'une
     * position recente etait disponible. Pour « ma position », une position de quelques minutes
     * vaut mieux qu'une carte centree par defaut. Jamais sans permission : ce serait contourner
     * le refus.
     */
    private suspend fun LocationResult?.orLastKnown(): LocationResult? {
        if (this is LocationResult.Available || this == LocationResult.PermissionMissing) return this
        val lastKnown = locationProvider.lastKnownLocation()
        if (lastKnown != null) Logger.i(TAG, "Fix frais indisponible, repli sur la dernière position connue")
        return lastKnown?.let { LocationResult.Available(it) } ?: this
    }

    /** Purge de session (#48) : le statut de permission est un etat de l'appareil, voir [MapViewModel.reset]. */
    fun reset() {
        lastPermissionStatus = null
    }
}

/**
 * Ce qu'il faut signaler quand la localisation n'a pas rendu de position. `null` (provider muet
 * dans le budget [LOCATION_TIMEOUT_MS]) rend la meme chose qu'une position indisponible : c'est ce
 * que l'utilisateur constate.
 */
private fun noticeFor(result: LocationResult?): LocationNotice =
    when (result) {
        LocationResult.PermissionMissing -> LocationNotice.PERMISSION_DENIED
        else -> LocationNotice.UNAVAILABLE
    }
